/**
 * SyncPushClient — SSE-based real-time sync receiver
 *
 * Connects to the storage node's `/api/stream` endpoint and listens for
 * `document:updated` events. When a notification arrives, it calls the
 * existing `syncFromRemote()` helper from documentSync.ts to pull and merge
 * the latest encrypted Yjs delta, making the change visible immediately
 * without waiting for the next poll cycle.
 *
 * Also surfaces presence information (which household members are online)
 * via a simple observable API.
 *
 * Architecture notes:
 * - One SSE connection per workspace session (not per document)
 * - Reconnects automatically with exponential back-off (max 30s)
 * - Falls back gracefully if SSE is not supported or the server is down
 * - The server sends only document IDs + version numbers, never plaintext
 * - Actual delta bytes are pulled via the existing HTTP storage API
 */

import { activeDocuments, syncFromRemote, syncAllWorkspaceDocuments, flushPendingSync } from './documentSync.js';
import { startSignaling, onSeedReady } from './signalingClient.js';
import { getAccessToken, refreshAccessToken, setAccessToken, clearAccessToken, getStorageUrl, AuthServiceUnavailableError } from '@yada/api-client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PresenceMember {
  profileId: string;
  connectedAt: string;
}

type SyncPushListener = (documentId: string) => void;
type PresenceListener = (members: PresenceMember[]) => void;

// ── Internal state ─────────────────────────────────────────────────────────────

interface WorkspaceStream {
  workspaceId: string;
  eventSource: EventSource | null;
  connecting: boolean;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconcileTimer: ReturnType<typeof setInterval> | null;
  reconnectDelayMs: number;
  reconciling: boolean;
}

const activeStreams = new Map<string, WorkspaceStream>();
let currentWorkspaceId: string | null = null;
let startSyncPushVersion = 0;

async function reconcileWorkspace(workspaceId: string): Promise<void> {
  const stream = activeStreams.get(workspaceId);
  if (!stream || stream.reconciling) return;
  stream.reconciling = true;
  try {
    await syncAllWorkspaceDocuments(workspaceId);
    await flushPendingSync();
  } catch (err) {
    console.warn(`[SyncPushClient] Reconciliation failed for workspace ${workspaceId}; will retry:`, err);
  } finally {
    if (stream) stream.reconciling = false;
  }
}
const MAX_RECONNECT_DELAY_MS = 30_000;
const workspaceCursors = new Map<string, string>();

const documentUpdateListeners = new Set<SyncPushListener>();
const presenceListeners = new Set<PresenceListener>();
let currentPresence: PresenceMember[] = [];

// Track documents currently executing syncFromRemote
const inFlightSyncs = new Set<string>();
// Track debounce timers per document ID
const pendingDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Track in-flight re-syncs. Value = latest rawData notification string
const pendingResyncs = new Map<string, { rawData: string; workspaceId?: string }>();

/**
 * Notify all registered document update listeners for a specific document.
 */
export function notifyDocumentUpdated(documentId: string): void {
  documentUpdateListeners.forEach(cb => {
    try { cb(documentId); } catch (err) {
      console.error('[SyncPushClient] documentUpdateListener error:', err);
    }
  });
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Start listening for real-time updates for a primary workspace and any additional workspaces.
 * Safe to call multiple times — updates streams as workspaces change.
 */
export async function startSyncPush(workspaceId: string, additionalWorkspaceIds?: string[]): Promise<void> {
  const callVersion = ++startSyncPushVersion;
  const account = await import('@yada/storage').then(s => s.getAccount());
  if (callVersion !== startSyncPushVersion) return;
  if (account?.isAccountless) {
    console.log('[SyncPushClient] Skipping sync push for accountless account');
    return;
  }

  const targetWorkspaceIds = new Set<string>([workspaceId, ...(additionalWorkspaceIds ?? [])]);

  // Stop any streams that are no longer in the target set
  for (const [id, stream] of activeStreams) {
    if (!targetWorkspaceIds.has(id)) {
      closeStream(stream);
      activeStreams.delete(id);
    }
  }

  currentWorkspaceId = workspaceId;

  // Start or keep stream for each target workspace
  for (const id of targetWorkspaceIds) {
    if (!activeStreams.has(id)) {
      const stream: WorkspaceStream = {
        workspaceId: id,
        eventSource: null,
        connecting: false,
        reconnectTimer: null,
        reconcileTimer: null,
        reconnectDelayMs: 1_000,
        reconciling: false,
      };
      activeStreams.set(id, stream);
      stream.reconcileTimer = setInterval(() => void reconcileWorkspace(id), 30_000);
      connect(id).catch(err =>
        console.error(`[SyncPushClient] Unexpected error during connect for workspace ${id}:`, err)
      );
    }
  }

  // Start the P2P signaling WebSocket alongside SSE for primary workspace.
  startSignaling().then(() => {
    onSeedReady(() => {
      window.dispatchEvent(new CustomEvent('yada:workspace:seed-received', { detail: { workspaceId } }));
    });
  }).catch(err => console.warn('[SyncPushClient] Signaling start failed:', err));

  // Listen for browser online and visibility events to trigger immediate reconnect and reconciliation
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
    window.addEventListener('online', handleOnline);
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
}

/**
 * Dynamically subscribe an additional workspace (e.g. newly shared list or chat workspace).
 */
export async function subscribeWorkspaceSync(workspaceId: string): Promise<void> {
  if (activeStreams.has(workspaceId)) return;
  const stream: WorkspaceStream = {
    workspaceId,
    eventSource: null,
    connecting: false,
    reconnectTimer: null,
    reconcileTimer: null,
    reconnectDelayMs: 1_000,
    reconciling: false,
  };
  activeStreams.set(workspaceId, stream);
  stream.reconcileTimer = setInterval(() => void reconcileWorkspace(workspaceId), 30_000);
  connect(workspaceId).catch(err =>
    console.error(`[SyncPushClient] Unexpected error during subscribe for workspace ${workspaceId}:`, err)
  );
}

/**
 * Unsubscribe a secondary workspace stream.
 */
export function unsubscribeWorkspaceSync(workspaceId: string): void {
  if (workspaceId === currentWorkspaceId) return; // Keep primary workspace active
  const stream = activeStreams.get(workspaceId);
  if (stream) {
    closeStream(stream);
    activeStreams.delete(workspaceId);
  }
}

function closeStream(stream: WorkspaceStream): void {
  stream.connecting = false;
  if (stream.reconcileTimer) clearInterval(stream.reconcileTimer);
  stream.reconcileTimer = null;
  if (stream.reconnectTimer) clearTimeout(stream.reconnectTimer);
  stream.reconnectTimer = null;
  if (stream.eventSource) {
    stream.eventSource.close();
    stream.eventSource = null;
  }
}

function handleVisibilityChange(): void {
  if (typeof document === 'undefined' || document.hidden) return;
  for (const workspaceId of activeStreams.keys()) {
    void reconcileWorkspace(workspaceId);
  }
}

function handleOnline(): void {
  if (!activeStreams.size) return;
  console.log('[SyncPushClient] Browser regained connectivity (online event), reconnecting all workspaces...');
  void flushPendingSync().catch(err =>
    console.warn('[SyncPushClient] Failed to flush pending sync on online event:', err)
  );
  for (const [workspaceId, stream] of activeStreams) {
    stream.reconnectDelayMs = 1_000;
    stream.connecting = false;
    if (stream.reconnectTimer) {
      clearTimeout(stream.reconnectTimer);
      stream.reconnectTimer = null;
    }
    if (stream.eventSource) {
      stream.eventSource.close();
      stream.eventSource = null;
    }
    connect(workspaceId).catch(err =>
      console.error(`[SyncPushClient] Reconnect on online event failed for workspace ${workspaceId}:`, err)
    );
  }
}

/**
 * Stop listening and close the SSE connections.
 */
export function stopSyncPush(): void {
  startSyncPushVersion++;
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  for (const stream of activeStreams.values()) {
    closeStream(stream);
  }
  activeStreams.clear();
  clearDeferredWorkspaceSyncs();
  currentWorkspaceId = null;
  currentPresence = [];
}

/**
 * Subscribe to document update notifications.
 * The callback fires after the in-memory Yjs doc has already been updated.
 * Consumers should re-read from their store to pick up the new state.
 */
export function onRemoteDocumentUpdate(cb: SyncPushListener): () => void {
  documentUpdateListeners.add(cb);
  return () => documentUpdateListeners.delete(cb);
}

/**
 * Subscribe to workspace presence changes (which members are online).
 * The callback is fired immediately with the current snapshot.
 */
export function onPresenceChange(cb: PresenceListener): () => void {
  presenceListeners.add(cb);
  cb(currentPresence); // immediate snapshot
  return () => presenceListeners.delete(cb);
}

/**
 * Get the current presence snapshot synchronously.
 */
export function getCurrentPresence(): PresenceMember[] {
  return currentPresence;
}

// ── Connection logic ──────────────────────────────────────────────────────────

function buildStreamUrl(workspaceId: string, ticket: string): string {
  const params = new URLSearchParams({ workspace_id: workspaceId, ticket });
  const cursor = workspaceCursors.get(workspaceId) || (typeof localStorage !== 'undefined' ? localStorage.getItem(`yada:cursor:${workspaceId}`) : null);
  if (cursor) params.set('since', cursor);
  return `${getStorageUrl()}/api/stream?${params.toString()}`;
}

/**
 * Reconnect the SSE push client to a (potentially new) storage node.
 * Call this after updating `localStorage['yada:defaultStorageUrl']` so the
 * new connection picks up the freshly written URL.
 */
export function reconnectSyncPush(): void {
  const workspaceId = currentWorkspaceId;
  if (!workspaceId) return;
  stopSyncPush();
  startSyncPush(workspaceId).catch(err =>
    console.error('[SyncPushClient] Unexpected error during reconnect:', err)
  );
}

/**
 * Resolve a valid access token, attempting a refresh if the stored token is
 * expired.  Returns `null` if the user is logged out and cannot be refreshed
 * (i.e. the session is fully expired).
 */
async function resolveToken(): Promise<string | null> {
  const token = getAccessToken();
  if (token) return token;

  // Token missing or expired — try a silent refresh via the httpOnly cookie.
  try {
    const refreshed = await refreshAccessToken();
    setAccessToken(refreshed.access_token, refreshed.expires_in);
    return refreshed.access_token;
  } catch (err) {
    // An outage or lost network says nothing about the session; retry later.
    if (err instanceof AuthServiceUnavailableError || err instanceof TypeError) throw err;
    clearAccessToken();
    return null;
  }
}

/**
 * EventSource cannot send an Authorization header, so the access token is
 * exchanged for a single-use feed ticket and only the ticket goes in the URL.
 * A rejected token is refreshed once, as for any storage 401.
 */
async function requestFeedTicket(workspaceId: string, token: string): Promise<string | null> {
  const mint = (bearer: string) => fetch(
    `${getStorageUrl()}/v1/ws/${encodeURIComponent(workspaceId)}/feed/ticket`,
    { method: 'POST', headers: { Authorization: `Bearer ${bearer}` } },
  );
  let response = await mint(token);
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    setAccessToken(refreshed.access_token, refreshed.expires_in);
    response = await mint(refreshed.access_token);
  }
  if (!response.ok) return null;
  const body = await response.json() as { ticket?: unknown };
  return typeof body.ticket === 'string' ? body.ticket : null;
}

async function connect(workspaceId: string): Promise<void> {
  const initialStream = activeStreams.get(workspaceId);
  if (!initialStream || initialStream.connecting || initialStream.eventSource) return;
  initialStream.connecting = true;

  let token: string | null;
  try {
    token = await resolveToken();
  } catch {
    initialStream.connecting = false;
    scheduleReconnect(workspaceId);
    return;
  }
  const stream = activeStreams.get(workspaceId);
  if (!stream || stream !== initialStream) {
    initialStream.connecting = false;
    return;
  }

  if (!token) {
    // Session is expired/unauthenticated — pause SSE and periodic remote reconciliation
    // until the user signs in again or regains valid credentials.
    stream.connecting = false;
    console.warn('[SyncPushClient] No valid token available; pausing SSE and remote reconciliation.');
    stopSyncPush();
    window.dispatchEvent(new CustomEvent('yada:session-expired'));
    return;
  }

  try {
    const ticket = await requestFeedTicket(workspaceId, token);
    if (activeStreams.get(workspaceId) !== stream) {
      stream.connecting = false;
      return;
    }
    if (!ticket) {
      stream.connecting = false;
      scheduleReconnect(workspaceId);
      return;
    }
    if (stream.eventSource) {
      stream.eventSource.close();
      stream.eventSource = null;
    }
    const url = buildStreamUrl(workspaceId, ticket);
    const es = new EventSource(url);
    stream.eventSource = es;
    stream.connecting = false;

    es.onopen = () => {
      console.log(`[SyncPushClient] SSE connected to workspace ${workspaceId}`);
      stream.reconnectDelayMs = 1_000; // reset back-off on success
      void reconcileWorkspace(workspaceId);
    };

    es.onerror = (err) => {
      console.warn(`[SyncPushClient] SSE error for workspace ${workspaceId}, scheduling reconnect`, err);
      stream.eventSource?.close();
      stream.eventSource = null;
      scheduleReconnect(workspaceId);
    };

    // ── Event handlers ────────────────────────────────────────────────────────

    es.addEventListener('connected', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        console.log(`[SyncPushClient] Handshake OK (${workspaceId}) — client ${data.clientId}, ${data.onlineCount} online`);
      } catch { /* ignore */ }
    });

    es.addEventListener('change', (e: MessageEvent) => {
      try {
        if (e.lastEventId) {
          workspaceCursors.set(workspaceId, e.lastEventId);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(`yada:cursor:${workspaceId}`, e.lastEventId);
          }
        }
        const feedEvent = JSON.parse(e.data);
        if (feedEvent && feedEvent.cursor) {
          workspaceCursors.set(workspaceId, feedEvent.cursor);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(`yada:cursor:${workspaceId}`, feedEvent.cursor);
          }
        }
        if (feedEvent && feedEvent.doc) {
          handleDocumentUpdated(JSON.stringify({
            documentId: feedEvent.doc,
            version: feedEvent.counter || 0,
            updatedByDeviceId: feedEvent.writer,
          }), workspaceId).catch(err =>
            console.error('[SyncPushClient] Error handling change event:', err)
          );
        }
      } catch (err) {
        console.warn('[SyncPushClient] Failed to parse change event payload:', err);
      }
    });

    es.addEventListener('presence:snapshot', (e: MessageEvent) => {
      try {
        const members: PresenceMember[] = JSON.parse(e.data);
        if (workspaceId === currentWorkspaceId) updatePresence(members);
      } catch { /* ignore */ }
    });

    es.addEventListener('member:joined', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (workspaceId === currentWorkspaceId && !currentPresence.some(m => m.profileId === data.profileId)) {
          updatePresence([...currentPresence, { profileId: data.profileId, connectedAt: data.connectedAt }]);
        }
      } catch { /* ignore */ }
    });

    es.addEventListener('member:left', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (workspaceId === currentWorkspaceId) {
          updatePresence(currentPresence.filter(m => m.profileId !== data.profileId));
        }
      } catch { /* ignore */ }
    });

  } catch (err) {
    stream.connecting = false;
    console.warn(`[SyncPushClient] Failed to open SSE connection for workspace ${workspaceId}:`, err);
    scheduleReconnect(workspaceId);
  }
}

function scheduleReconnect(workspaceId: string): void {
  const stream = activeStreams.get(workspaceId);
  if (!stream || stream.reconnectTimer) return;

  console.log(`[SyncPushClient] Reconnecting workspace ${workspaceId} in ${stream.reconnectDelayMs}ms`);
  stream.reconnectTimer = setTimeout(() => {
    if (stream) {
      stream.reconnectTimer = null;
    }
    if (activeStreams.has(workspaceId)) {
      connect(workspaceId).catch(err =>
        console.error(`[SyncPushClient] Unexpected error during reconnect for workspace ${workspaceId}:`, err)
      );
    }
  }, stream.reconnectDelayMs);

  stream.reconnectDelayMs = Math.min(stream.reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
}

// Track last remote sync time per document to coalesce rapid SSE bursts
const lastRemoteSyncTimestamps = new Map<string, number>();
const MIN_DOCUMENT_SYNC_INTERVAL_MS = 1_000;
let lastSyncAllTimestamp = 0;
const MIN_SYNC_ALL_INTERVAL_MS = 15_000;
// Notifications for unopened documents that arrived inside the throttle window,
// per workspace. They get one trailing sync instead of being dropped.
const deferredWorkspaceSyncs = new Map<string, { timer: ReturnType<typeof setTimeout>; documentIds: Set<string> }>();

function runWorkspaceSync(workspaceId: string, documentIds: Iterable<string>): void {
  lastSyncAllTimestamp = Date.now();
  const waiting = [...documentIds];
  syncAllWorkspaceDocuments(workspaceId)
    .then(() => {
      // If a document is now open, notify listeners so the UI refreshes.
      for (const documentId of waiting) {
        if (activeDocuments.has(documentId)) notifyDocumentUpdated(documentId);
      }
    })
    .catch(err =>
      console.warn('[SyncPushClient] Failed to open workspace documents on new-doc notification:', err)
    );
}

function syncWorkspaceForUnopenedDocument(workspaceId: string, documentId: string, now: number): void {
  const deferred = deferredWorkspaceSyncs.get(workspaceId);
  if (deferred) {
    deferred.documentIds.add(documentId);
    return;
  }
  const wait = MIN_SYNC_ALL_INTERVAL_MS - (now - lastSyncAllTimestamp);
  if (wait < 0) {
    runWorkspaceSync(workspaceId, [documentId]);
    return;
  }
  const documentIds = new Set([documentId]);
  const timer = setTimeout(() => {
    deferredWorkspaceSyncs.delete(workspaceId);
    runWorkspaceSync(workspaceId, documentIds);
  }, wait);
  deferredWorkspaceSyncs.set(workspaceId, { timer, documentIds });
}

function clearDeferredWorkspaceSyncs(): void {
  for (const { timer } of deferredWorkspaceSyncs.values()) clearTimeout(timer);
  deferredWorkspaceSyncs.clear();
}

// ── Delta application ──────────────────────────────────────────────────────────

async function handleDocumentUpdated(rawData: string, workspaceId?: string): Promise<void> {
  let notification: { documentId: string; version: number; updatedByProfileId?: string; updatedByDeviceId?: string };
  try {
    notification = JSON.parse(rawData);
  } catch {
    console.warn('[SyncPushClient] Malformed document:updated payload:', rawData);
    return;
  }

  const { documentId, updatedByDeviceId } = notification;

  // Skip notifications caused by our own writes — we already applied them locally.
  const myDeviceId = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:deviceId') : null;
  if (updatedByDeviceId && myDeviceId && updatedByDeviceId === myDeviceId) {
    return;
  }

  // If a sync is already in flight for this document, record the latest notification
  // so it runs immediately when the current sync finishes without getting lost.
  if (inFlightSyncs.has(documentId)) {
    pendingResyncs.set(documentId, { rawData, workspaceId });
    return;
  }

  // Rate limit incoming pulls per document ID
  const now = Date.now();
  const lastSync = lastRemoteSyncTimestamps.get(documentId) || 0;
  if (now - lastSync < MIN_DOCUMENT_SYNC_INTERVAL_MS) {
    if (!pendingDebounceTimers.has(documentId)) {
      pendingResyncs.set(documentId, { rawData, workspaceId });
      const timer = setTimeout(() => {
        pendingDebounceTimers.delete(documentId);
        const pending = pendingResyncs.get(documentId);
        if (pending) {
          pendingResyncs.delete(documentId);
          handleDocumentUpdated(pending.rawData, pending.workspaceId).catch(() => {});
        }
      }, MIN_DOCUMENT_SYNC_INTERVAL_MS);
      pendingDebounceTimers.set(documentId, timer);
    } else {
      pendingResyncs.set(documentId, { rawData, workspaceId });
    }
    return;
  }

  // Is this document currently open in memory?
  const synced = activeDocuments.get(documentId);
  if (!synced) {
    // Document not open yet — sync the workspace, at most once per throttle window
    const wsToSync = workspaceId || currentWorkspaceId;
    if (wsToSync) syncWorkspaceForUnopenedDocument(wsToSync, documentId, now);
    return;
  }

  inFlightSyncs.add(documentId);
  lastRemoteSyncTimestamps.set(documentId, Date.now());

  try {
    // Re-use the existing syncFromRemote logic which handles decrypt + Y.applyUpdate
    await syncFromRemote(synced);

    // Notify subscribers that this document changed (UI should re-read from store)
    notifyDocumentUpdated(documentId);

    console.log(`[SyncPushClient] Applied remote update to ${documentId}`);
  } catch (err) {
    console.error(`[SyncPushClient] Failed to sync ${documentId}:`, err);
  } finally {
    inFlightSyncs.delete(documentId);
    const retry = pendingResyncs.get(documentId);
    if (retry) {
      pendingResyncs.delete(documentId);
      handleDocumentUpdated(retry.rawData, retry.workspaceId).catch(err =>
        console.error('[SyncPushClient] Retry sync failed for', documentId, err)
      );
    }
  }
}

// ── Presence helpers ──────────────────────────────────────────────────────────

function updatePresence(members: PresenceMember[]): void {
  currentPresence = members;
  presenceListeners.forEach(cb => {
    try { cb(members); } catch { /* ignore */ }
  });
}
